import { useEffect, useRef, useState } from "react";

import { fileUrl, type ReviewItem } from "./api";
import { wordAt } from "./content";

// RV-6 — read-along player (the staging app's player, in Launch OS). Plays a brief or an item's
// read-aloud with a scrubber, the time and speed (0.75× to 2.5×); the words light up as they're spoken (from the
// word timings saved with the audio), and tapping a word jumps there. Audio without timings still
// plays, with the scrubber only.

type Timing = { tokens: string[]; times: ([number, number] | null)[] };
const clock = (s: number) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5];

export function ReadAlong({ reviewId, audio, times, label, onClose }: { reviewId: string; audio: string; times?: string; label: string; onClose: () => void }) {
  const ref = useRef<HTMLAudioElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [timing, setTiming] = useState<Timing | null>(null);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [on, setOn] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    setTiming(null); setT(0); setErr("");
    fileUrl(reviewId, audio)
      .then((u) => { if (live && ref.current) { ref.current.src = u; ref.current.play().catch(() => {}); } })
      .catch(() => live && setErr("Couldn't load the audio."));
    if (times) fileUrl(reviewId, times).then((u) => fetch(u)).then((r) => r.json()).then((j) => live && j?.tokens && setTiming(j)).catch(() => {});
    return () => { live = false; ref.current?.pause(); };
  }, [reviewId, audio, times]);

  // smooth highlight while playing
  useEffect(() => {
    if (!on) return;
    let raf = 0;
    const tick = () => { if (ref.current) setT(ref.current.currentTime); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [on]);

  const lit = timing ? wordAt(timing.times, t) : -1;
  useEffect(() => {
    const el = textRef.current?.querySelector<HTMLElement>(`[data-w="${lit}"]`);
    if (el && textRef.current) {
      const box = textRef.current, top = el.offsetTop - box.offsetTop;
      if (top < box.scrollTop + 8 || top > box.scrollTop + box.clientHeight - 28) box.scrollTo({ top: Math.max(0, top - box.clientHeight / 3), behavior: "smooth" });
    }
  }, [lit]);

  const seek = (s: number) => { if (ref.current) { ref.current.currentTime = s; setT(s); } };
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) a.play().catch(() => {}); else a.pause(); };
  const pickSpeed = (n: number) => { setSpeed(n); if (ref.current) ref.current.playbackRate = n; };

  return (
    <div className="lr-player" role="region" aria-label={`Audio: ${label}`}>
      <div className="top"><b>{label}</b><button onClick={onClose} aria-label="Close the player">✕</button></div>
      {timing && (
        <div className="words" ref={textRef}>
          {timing.tokens.map((w, i) =>
            timing.times[i] ? <span key={i} data-w={i} className={i === lit ? "lit" : i < lit ? "past" : ""} onClick={() => seek(timing.times[i]![0])}>{w}</span> : <span key={i}>{w}</span>,
          )}
        </div>
      )}
      {err && <div className="lr-error" style={{ margin: 0 }}>{err}</div>}
      <div className="ctl">
        <button className="pp" onClick={toggle} aria-label={on ? "Pause" : "Play"}>{on ? "❚❚" : "▶"}</button>
        <span className="tm">{clock(t)}</span>
        <input type="range" min={0} max={dur || 0} step={0.1} value={Math.min(t, dur || 0)} onChange={(e) => seek(Number(e.target.value))} aria-label="Scrub" />
        <span className="tm">{clock(dur)}</span>
        <select className="sp" value={speed} onChange={(e) => pickSpeed(Number(e.target.value))} aria-label="Playback speed">
          {SPEEDS.map((n) => <option key={n} value={n}>{n}×</option>)}
        </select>
      </div>
      <audio ref={ref} preload="auto" playsInline onPlay={() => setOn(true)} onPause={() => setOn(false)} onEnded={() => setOn(false)} onLoadedMetadata={(e) => { setDur(e.currentTarget.duration); e.currentTarget.playbackRate = speed; }} onTimeUpdate={(e) => !on && setT(e.currentTarget.currentTime)} />
    </div>
  );
}

// an item's Brief and "Read it to me" buttons (Short form tray, Questions cards, Decision room).
// `now` is the audio file the room's player has open; tapping the same one again stops it.
export type PlayKind = "brief" | "read";
export function ItemAudio({ it, now, onPlay }: { it: ReviewItem; now: string | null; onPlay: (it: ReviewItem, kind: PlayKind) => void }) {
  const briefOn = !!it.briefAudio && now === it.briefAudio;
  return (
    <>
      {it.brief && (
        <button className="lr-brief" onClick={() => onPlay(it, "brief")} disabled={!it.briefAudio} aria-label={briefOn ? "Pause the brief" : "Play the brief"}>
          <span className="pb">{briefOn ? "❚❚" : "▶"}</span>
          <span className="bl">Brief · {Math.max(10, Math.round(it.brief.split(/\s+/).length / 2.6))}s</span>
          <span className="bt">{it.brief}</span>
        </button>
      )}
      {it.readAudio && <button className="lr-listen" onClick={() => onPlay(it, "read")}>{now === it.readAudio ? "❚❚ Stop reading" : "▶ Read it to me"}</button>}
    </>
  );
}
